<template>
    <DialogFrame :show-dialog="show"
                 :on-click-close="onClose"
                 :title="'パスワードを忘れたら'"
                 :width="400"
                 :height="300"
    >
        <template v-slot:dialogContent>
            <div class="container-fluid h-100">

                <!-- Step: email -->
                <template v-if="step === 'email'">
                    <div class="row mt-3">
                        <div class="col">
                            <label class="form-label">メールアドレス</label>
                            <input v-model="email"
                                   type="email"
                                   class="form-control"
                                   placeholder="example@email.com" />
                        </div>
                    </div>
                    <div class="d-flex mt-4" style="gap: 12px;">
                        <button class="flex-fill btn btn-primary"
                                :disabled="!email"
                                @click="onSubmitEmail">次へ</button>
                        <button class="flex-fill btn btn-secondary"
                                @click="onClose">閉じる</button>
                    </div>
                </template>

                <!-- Step: loading -->
                <template v-else-if="step === 'loading'">
                    <div class="d-flex justify-content-center align-items-center" style="height: 150px;">
                        <div class="spinner-border text-primary" role="status"></div>
                    </div>
                </template>

                <!-- Step: select staff -->
                <template v-else-if="step === 'select'">
                    <div class="row mt-3">
                        <div class="col">
                            <p v-if="staffList.length > 1" class="mb-2 select-label">社員IDを選択してください</p>
                            <div class="staff-list">
                                <div v-for="item in staffList"
                                     :key="item.staff_id"
                                     class="staff-item"
                                     :class="{ selected: selectedStaffId === item.staff_id }"
                                     @click="selectedStaffId = item.staff_id">
                                    社員ID: {{ item.staff_id }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex mt-3" style="gap: 12px;">
                        <button class="flex-fill btn btn-primary"
                                :disabled="selectedStaffId === null"
                                @click="onSubmitStaffId">送信</button>
                        <button class="flex-fill btn btn-secondary"
                                @click="step = 'email'">戻る</button>
                    </div>
                </template>

                <!-- Step: sent -->
                <template v-else-if="step === 'sent'">
                    <div class="d-flex flex-column align-items-center justify-content-center h-100">
                        <p class="text-center mb-3">パスワードリセットのメールを送信しました。</p>
                        <button class="btn btn-secondary w-75" @click="onClose">閉じる</button>
                    </div>
                </template>

            </div>
        </template>
    </DialogFrame>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import DialogFrame from "@/components/DialogFrame.vue";
import { backendApi } from "@/utils/backend-api";

type Step = 'email' | 'loading' | 'select' | 'sent'

export default defineComponent({
    name: "PasswordForgetDialog",
    components: {
        DialogFrame,
    },
    props: {
        show: {
            type: Boolean,
            required: true
        },
        closeDialog: {
            type: Function as PropType<() => void>,
            required: true
        }
    },
    data() {
        return {
            step: 'email' as Step,
            email: '',
            staffList: [] as { staff_id: number }[],
            selectedStaffId: null as number | null,
        }
    },
    methods: {
        onClose() {
            this.step = 'email'
            this.email = ''
            this.staffList = []
            this.selectedStaffId = null
            this.closeDialog()
        },
        async onSubmitEmail() {
            this.step = 'loading'
            const res = await backendApi.getStaffListByEmail(this.email)
            if (res.isSuccessful && Array.isArray(res.value)) {
                this.staffList = res.value
                this.selectedStaffId = res.value[0]?.staff_id ?? null
                this.step = 'select'
            } else {
                this.step = 'email'
            }
        },
        async onSubmitStaffId() {
            if (this.selectedStaffId === null) return
            this.step = 'loading'
            await backendApi.reqForgotPassword(this.selectedStaffId)
            this.step = 'sent'
        },
    }
})
</script>

<style scoped lang="scss">
.select-label {
    font-size: 0.9em;
}

.staff-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 100px;
    overflow-y: auto;
}

.staff-item {
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;

    &:hover {
        background-color: #f0f4ff;
    }

    &.selected {
        border-color: #0d6efd;
        background-color: #e7f0ff;
        font-weight: 600;
    }
}
</style>
